<div class="flex max-w-4xl flex-col gap-4">
  @if (identifiers().length) {
    <div class="flex flex-col gap-1.5">
      <span class="w-fit rounded bg-neutral-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-neutral-600 uppercase">
        {{ "protocols.edit.identifiers" | translate }}
      </span>
      <div class="flex flex-wrap gap-1.5">
        @for (identifier of identifiers(); track $index) {
          <span class="rounded bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-700 ring-1 ring-neutral-200">{{ identifier }}</span>
        }
      </div>
    </div>
  }

  @if (protocol().coverage.length) {
    <div class="flex flex-col gap-1.5">
      <span class="w-fit rounded bg-neutral-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-neutral-600 uppercase">
        {{ "protocols.detail.coverage" | translate }}
      </span>
      <div class="flex flex-wrap gap-1.5">
        @for (tag of protocol().coverage; track tag) {
          <span class="bg-primary-50 text-primary-600 ring-primary-100 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1">{{ tag }}</span>
        }
      </div>
    </div>
  }

  @for (section of sections(); track section.titleKey) {
    <div class="flex flex-col gap-1.5">
      <span class="w-fit rounded bg-neutral-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-neutral-600 uppercase">
        {{ section.titleKey | translate }}
      </span>
      <div class="flex flex-col gap-1">
        @for (item of section.items; track $index) {
          <div class="flex items-start gap-2">
            @if (section.showCode) {
              <span
                class="bg-primary-50 text-primary-700 ring-primary-100 mt-0.5 shrink-0 rounded px-2 py-0.5 text-center text-xs font-semibold whitespace-nowrap ring-1 {{ section.codeWidthClass }}"
                >{{ item.code }}</span
              >
            }
            <span class="min-w-0 break-words text-xs text-neutral-700">{{ item.nameUa || item.nameEn }}</span>
          </div>
        }
      </div>
    </div>
  }

  @if (pharmacological().length) {
    <div class="flex flex-col gap-1.5">
      <span class="w-fit rounded bg-neutral-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-neutral-600 uppercase">
        {{ "protocols.detail.section.pharmacologicalTreatments" | translate }}
      </span>
      <div class="flex flex-col gap-2">
        @for (entry of pharmacological(); track $index) {
          <div class="flex flex-col gap-1 border-s-2 border-neutral-100 ps-2.5">
            @if (entry.trademarks.length) {
              <button
                type="button"
                class="flex w-fit flex-wrap items-center gap-2 text-start"
                (click)="toggleInn(entry.inn.code)"
              >
                <span class="text-xs text-neutral-400">{{ isInnExpanded(entry.inn.code) ? "▾" : "▸" }}</span>
                <span class="text-xs font-semibold text-neutral-700">{{ entry.inn.nameUa || entry.inn.nameEn || entry.inn.code }}</span>
                <span class="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[0.65rem] font-semibold text-neutral-500">
                  {{ "protocols.detail.trademarks.count" | translate: { count: entry.trademarks.length } }}
                </span>
              </button>
            } @else {
              <span class="text-xs font-semibold text-neutral-700">{{ entry.inn.nameUa || entry.inn.nameEn || entry.inn.code }}</span>
            }
            @if (entry.trademarks.length && isInnExpanded(entry.inn.code)) {
              <div class="flex flex-col gap-1">
                @for (group of entry.trademarks; track $index) {
                  <div class="flex items-start gap-2">
                    <span class="min-w-0 break-words text-xs text-neutral-700">
                      {{ group.name }}
                      @if (group.morionCodes.length) {
                        <span class="text-neutral-400">({{ group.morionCodes.join(", ") }})</span>
                      }
                    </span>
                    @if (group.isRecommended || group.requiresRecipe) {
                      <div class="mt-0.5 flex shrink-0 items-center gap-1">
                        @if (group.isRecommended) {
                          <span class="bg-success-50 text-success-700 rounded px-1.5 py-0.5 text-[0.65rem] font-semibold">
                            {{ "protocols.detail.trademarks.recommended" | translate }}
                          </span>
                        }
                        @if (group.requiresRecipe) {
                          <span class="bg-warning-50 text-warning-700 rounded px-1.5 py-0.5 text-[0.65rem] font-semibold">
                            {{ "protocols.detail.trademarks.requiresRecipe" | translate }}
                          </span>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>
  }

  @if (generalRecommendations().length) {
    <div class="flex flex-col gap-1.5">
      <span class="w-fit rounded bg-neutral-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-neutral-600 uppercase">
        {{ "protocols.detail.section.generalRecommendations" | translate }}
      </span>
      <ul class="list-disc ps-5 text-xs text-neutral-700">
        @for (item of generalRecommendations(); track $index) {
          <li class="break-words">{{ item }}</li>
        }
      </ul>
    </div>
  }

  @for (section of textSections(); track section.titleKey) {
    <div class="flex flex-col gap-1.5">
      <span class="w-fit rounded bg-neutral-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-neutral-600 uppercase">
        {{ section.titleKey | translate }}
      </span>
      <ul class="list-disc ps-5 text-xs text-neutral-700">
        @for (item of section.items; track $index) {
          <li>{{ item }}</li>
        }
      </ul>
    </div>
  }

  @if (unresolvedGroups().length) {
    <div class="flex flex-col gap-1.5">
      <span class="bg-error-50 text-error-700 w-fit rounded px-2 py-0.5 text-xs font-semibold tracking-wide uppercase">
        {{ "protocols.codeView.notFound" | translate }}
      </span>
      <div class="flex flex-col gap-1.5">
        @for (group of unresolvedGroups(); track group.label) {
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="text-error-600 shrink-0 text-xs font-semibold">{{ group.label }}:</span>
            @for (code of group.codes; track $index) {
              <span class="bg-error-50 text-error-700 ring-error-200 rounded px-2 py-0.5 text-xs font-semibold ring-1">{{ code }}</span>
            }
          </div>
        }
      </div>
    </div>
  }
</div>
